import React, { useState, useMemo } from "react";
import {
  HeartPulse,
  Zap,
  Activity,
  Flame,
  ShieldCheck,
  Info,
  Users,
  SlidersHorizontal,
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight,
} from "lucide-react";
import { QUADRANT_DIMENSIONS, buildQuadrantDataset } from "./helpers";
import WidgetInfoTooltip from "./WidgetInfoTooltip";

export default function HealthQuadrantScatter({ employees = [], forceSimulated = false }) {
  const [activeDimension, setActiveDimension] = useState("cardiometabolic");
  const [selectedQuadrant, setSelectedQuadrant] = useState(null); // null = all, or "q1", "q2", "q3", "q4"
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Construir dataset reactivo según dimensión activa y simulación
  const dataset = useMemo(() => {
    return buildQuadrantDataset(employees, activeDimension, forceSimulated);
  }, [employees, activeDimension, forceSimulated]);

  const { dimension, points, summary, totalCount, isSimulated } = dataset;

  // Configuración geométrica del plano cartesiano SVG
  const svgWidth = 560;
  const svgHeight = 380;
  const margin = { top: 35, right: 35, bottom: 55, left: 65 };
  const plotWidth = svgWidth - margin.left - margin.right;
  const plotHeight = svgHeight - margin.top - margin.bottom;

  // Funciones de escala lineal
  const scaleX = (val) => {
    const clamped = Math.max(dimension.xMin, Math.min(dimension.xMax, val));
    return margin.left + ((clamped - dimension.xMin) / (dimension.xMax - dimension.xMin)) * plotWidth;
  };

  const scaleY = (val) => {
    const clamped = Math.max(dimension.yMin, Math.min(dimension.yMax, val));
    // Eje Y invertido en SVG (yMin abajo, yMax arriba)
    return margin.top + plotHeight - ((clamped - dimension.yMin) / (dimension.yMax - dimension.yMin)) * plotHeight;
  };

  const xCutPx = scaleX(dimension.xCut);
  const yCutPx = scaleY(dimension.yCut);

  // Ticks para los ejes
  const xTicks = useMemo(() => {
    const step = (dimension.xMax - dimension.xMin) / 4;
    return [0, 1, 2, 3, 4].map((i) => Number((dimension.xMin + i * step).toFixed(activeDimension === "metabolic" ? 2 : 1)));
  }, [dimension, activeDimension]);

  const yTicks = useMemo(() => {
    const step = (dimension.yMax - dimension.yMin) / 4;
    return [0, 1, 2, 3, 4].map((i) => Math.round(dimension.yMin + i * step));
  }, [dimension]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm space-y-5" data-testid="health-quadrant-scatter">
      {/* ── Header con Título & Badges de Privacidad ─────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              <ShieldCheck size={12} />
              Privacidad SST: Puntos 100% Desasociados & Anonimizados
            </span>
            {isSimulated && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                Cohorte Demostrativa
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <h3 className="text-base font-bold text-slate-900">
              Matriz de Dispersión Poblacional (4 Cuadrantes de Riesgo)
            </h3>
            <WidgetInfoTooltip
              title="Matriz de Dispersión Epidemiológica (4 Cuadrantes)"
              align="left"
              what="Mapeo bivariado de la fuerza laboral que correlaciona dos factores de riesgo clínicos u ocupacionales en un plano cartesiano interactivo."
              calculation="Cada punto representa un colaborador 100% anonimizado. Los cuadrantes (Q1 a Q4) se delimitan mediante líneas de corte de guías clínicas oficiales (OMS, AHA, NSF)."
              standard="ACC/AHA 2017 (HTA) • ADA 2024 (Glucemia) • IDF/Ashwell 2012 (Grasa Visceral) • Baevsky RM (Estrés) • NSF (Sueño) • OMS IPAQ (Actividad)."
              relevance="Permite a Medicina Ocupacional y SST identificar sub-poblaciones con riesgo compuesto (ej. hipertensos con obesidad o colaboradores con burnout y privación de sueño) para intervenir con planes duales."
            />
          </div>
          <p className="text-xs text-slate-500">
            Co-ocurrencia y concentración epidemiológica de factores de riesgo en la plantilla laboral.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/60">
          <Users size={14} className="text-slate-400" />
          <span>Colaboradores analizados: <strong className="text-slate-800">{totalCount}</strong></span>
        </div>
      </div>

      {/* ── Selector de Patologías / Dimensiones Clínicas ────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => { setActiveDimension("cardiometabolic"); setSelectedQuadrant(null); }}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
            activeDimension === "cardiometabolic"
              ? "bg-rose-50 text-rose-800 border border-rose-300 shadow-xs"
              : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
          }`}
          data-testid="dimension-cardiometabolic-btn"
        >
          <HeartPulse size={14} className={activeDimension === "cardiometabolic" ? "text-rose-600" : "text-slate-400"} />
          Cardiometabólico (PA vs. IMC)
        </button>

        <button
          type="button"
          onClick={() => { setActiveDimension("mental"); setSelectedQuadrant(null); }}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
            activeDimension === "mental"
              ? "bg-indigo-50 text-indigo-800 border border-indigo-300 shadow-xs"
              : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
          }`}
          data-testid="dimension-mental-btn"
        >
          <Zap size={14} className={activeDimension === "mental" ? "text-indigo-600" : "text-slate-400"} />
          Salud Mental (Estrés vs. Sueño)
        </button>

        <button
          type="button"
          onClick={() => { setActiveDimension("activity"); setSelectedQuadrant(null); }}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
            activeDimension === "activity"
              ? "bg-teal-50 text-teal-800 border border-teal-300 shadow-xs"
              : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
          }`}
          data-testid="dimension-activity-btn"
        >
          <Activity size={14} className={activeDimension === "activity" ? "text-teal-600" : "text-slate-400"} />
          Actividad (METs vs. Sedentarismo)
        </button>

        <button
          type="button"
          onClick={() => { setActiveDimension("metabolic"); setSelectedQuadrant(null); }}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
            activeDimension === "metabolic"
              ? "bg-amber-50 text-amber-800 border border-amber-300 shadow-xs"
              : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
          }`}
          data-testid="dimension-metabolic-btn"
        >
          <Flame size={14} className={activeDimension === "metabolic" ? "text-amber-600" : "text-slate-400"} />
          Metabólico (Glucemia vs. WHtR)
        </button>
      </div>

      {/* ── Subtítulo de Dimensión Seleccionada ──────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-slate-500 bg-slate-50/70 p-3 rounded-xl border border-slate-200/50">
        <div className="flex items-center gap-2">
          <div>
            <strong className="text-slate-800">{dimension.title}:</strong> {dimension.subtitle}
          </div>
          <WidgetInfoTooltip
            title={`Dimensión: ${dimension.title}`}
            size="sm"
            align="left"
            what={`Correlación bivariada entre ${dimension.xLabel} (Eje X) y ${dimension.yLabel} (Eje Y).`}
            calculation={`Líneas de corte clínico: Eje X = ${dimension.xCut} ${dimension.xUnit} | Eje Y = ${dimension.yCut} ${dimension.yUnit}.`}
            standard={
              activeDimension === "cardiometabolic"
                ? "ACC/AHA 2017 (PA ≥ 130 mmHg) & OMS 2000 (IMC ≥ 25 kg/m²)"
                : activeDimension === "mental"
                ? "Baevsky RM Stress Index (> 400 pts) & NSF Consensus (< 7h sueño)"
                : activeDimension === "activity"
                ? "OMS Guidelines 2020 & IPAQ Research Committee"
                : "ADA Standards of Care 2024 (Glucosa ≥ 100 mg/dL) & IDF / Ashwell 2012 (WHtR > 0.50)"
            }
            relevance="Identifica con precisión matemática qué colaboradores presentan riesgo compuesto para planificar programas de intervención conjunta."
          />
        </div>
        <div className="text-[11px] text-slate-400">
          Líneas de corte clínico: <span className="font-mono text-slate-600">X = {dimension.xCut} {dimension.xUnit}</span> | <span className="font-mono text-slate-600">Y = {dimension.yCut} {dimension.yUnit}</span>
        </div>
      </div>

      {/* ── Grid Principal: Gráfico SVG (Izquierda) + Resumen de Cuadrantes (Derecha) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Columna Izquierda: Lienzo Cartesiano SVG */}
        <div className="lg:col-span-7 bg-slate-900/[0.02] border border-slate-200/90 rounded-2xl p-3 sm:p-4 relative">
          <div className="w-full overflow-x-auto">
            <svg
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              className="w-full h-auto select-none"
              style={{ minWidth: "460px" }}
            >
              {/* Fondos sombreados para los 4 cuadrantes */}
              {/* Cuadrante 2 (Superior Izquierdo) */}
              <rect
                x={margin.left}
                y={margin.top}
                width={Math.max(0, xCutPx - margin.left)}
                height={Math.max(0, yCutPx - margin.top)}
                fill={dimension.q2.color}
                opacity={selectedQuadrant && selectedQuadrant !== "q2" ? 0.02 : 0.06}
                rx={8}
              />
              {/* Cuadrante 4 (Superior Derecho) */}
              <rect
                x={xCutPx}
                y={margin.top}
                width={Math.max(0, margin.left + plotWidth - xCutPx)}
                height={Math.max(0, yCutPx - margin.top)}
                fill={dimension.q4.color}
                opacity={selectedQuadrant && selectedQuadrant !== "q4" ? 0.02 : 0.08}
                rx={8}
              />
              {/* Cuadrante 1 (Inferior Izquierdo) */}
              <rect
                x={margin.left}
                y={yCutPx}
                width={Math.max(0, xCutPx - margin.left)}
                height={Math.max(0, margin.top + plotHeight - yCutPx)}
                fill={dimension.q1.color}
                opacity={selectedQuadrant && selectedQuadrant !== "q1" ? 0.02 : 0.06}
                rx={8}
              />
              {/* Cuadrante 3 (Inferior Derecho) */}
              <rect
                x={xCutPx}
                y={yCutPx}
                width={Math.max(0, margin.left + plotWidth - xCutPx)}
                height={Math.max(0, margin.top + plotHeight - yCutPx)}
                fill={dimension.q3.color}
                opacity={selectedQuadrant && selectedQuadrant !== "q3" ? 0.02 : 0.06}
                rx={8}
              />

              {/* Grilla sutil */}
              {xTicks.map((val, idx) => {
                const xPos = scaleX(val);
                return (
                  <line
                    key={`grid-x-${idx}`}
                    x1={xPos}
                    y1={margin.top}
                    x2={xPos}
                    y2={margin.top + plotHeight}
                    stroke="#E2E8F0"
                    strokeWidth={1}
                    strokeDasharray="2 3"
                  />
                );
              })}
              {yTicks.map((val, idx) => {
                const yPos = scaleY(val);
                return (
                  <line
                    key={`grid-y-${idx}`}
                    x1={margin.left}
                    y1={yPos}
                    x2={margin.left + plotWidth}
                    y2={yPos}
                    stroke="#E2E8F0"
                    strokeWidth={1}
                    strokeDasharray="2 3"
                  />
                );
              })}

              {/* Líneas de Corte Umbral (Cruces de Cuadrantes) */}
              <line
                x1={xCutPx}
                y1={margin.top}
                x2={xCutPx}
                y2={margin.top + plotHeight}
                stroke="#64748B"
                strokeWidth={1.8}
                strokeDasharray="4 4"
              />
              <line
                x1={margin.left}
                y1={yCutPx}
                x2={margin.left + plotWidth}
                y2={yCutPx}
                stroke="#64748B"
                strokeWidth={1.8}
                strokeDasharray="4 4"
              />

              {/* Etiquetas de Umbral de Corte */}
              <g transform={`translate(${xCutPx + 4}, ${margin.top + 14})`}>
                <rect x={0} y={-11} width={64} height={16} rx={4} fill="#FFFFFF" fillOpacity={0.92} stroke="#CBD5E1" strokeWidth={0.8} />
                <text x={4} y={1} fontSize="9" fontWeight="bold" fill="#475569" dangerouslySetInnerHTML={{ __html: `Corte: ${dimension.xCut}` }} />
              </g>

              <g transform={`translate(${margin.left + plotWidth - 66}, ${yCutPx - 6})`}>
                <rect x={-4} y={-11} width={64} height={16} rx={4} fill="#FFFFFF" fillOpacity={0.92} stroke="#CBD5E1" strokeWidth={0.8} />
                <text x={0} y={1} fontSize="9" fontWeight="bold" fill="#475569" dangerouslySetInnerHTML={{ __html: `Corte: ${dimension.yCut}` }} />
              </g>

              {/* Ejes principales X e Y */}
              <line
                x1={margin.left}
                y1={margin.top + plotHeight}
                x2={margin.left + plotWidth}
                y2={margin.top + plotHeight}
                stroke="#94A3B8"
                strokeWidth={1.5}
              />
              <line
                x1={margin.left}
                y1={margin.top}
                x2={margin.left}
                y2={margin.top + plotHeight}
                stroke="#94A3B8"
                strokeWidth={1.5}
              />

              {/* Ticks y valores del Eje X */}
              {xTicks.map((val, idx) => {
                const xPos = scaleX(val);
                return (
                  <g key={`tick-x-${idx}`} transform={`translate(${xPos}, ${margin.top + plotHeight})`}>
                    <line x1={0} y1={0} x2={0} y2={5} stroke="#94A3B8" strokeWidth={1} />
                    <text x={0} y={16} fontSize="10" fill="#64748B" textAnchor="middle">
                      {val}
                    </text>
                  </g>
                );
              })}

              {/* Ticks y valores del Eje Y */}
              {yTicks.map((val, idx) => {
                const yPos = scaleY(val);
                return (
                  <g key={`tick-y-${idx}`} transform={`translate(${margin.left}, ${yPos})`}>
                    <line x1={-5} y1={0} x2={0} y2={0} stroke="#94A3B8" strokeWidth={1} />
                    <text x={-9} y={3} fontSize="10" fill="#64748B" textAnchor="end">
                      {val}
                    </text>
                  </g>
                );
              })}

              {/* Título de los Ejes */}
              <text
                x={margin.left + plotWidth / 2}
                y={svgHeight - 12}
                fontSize="11"
                fontWeight="600"
                fill="#334155"
                textAnchor="middle"
                dangerouslySetInnerHTML={{ __html: `${dimension.xLabel} (${dimension.xUnit})` }}
              />

              <text
                x={-(margin.top + plotHeight / 2)}
                y={16}
                transform="rotate(-90)"
                fontSize="11"
                fontWeight="600"
                fill="#334155"
                textAnchor="middle"
                dangerouslySetInnerHTML={{ __html: `${dimension.yLabel} (${dimension.yUnit})` }}
              />

              {/* Puntos de los colaboradores (Anonimizados) */}
              {points.map((pt) => {
                const cx = scaleX(pt.x);
                const cy = scaleY(pt.y);
                const isSelected = selectedQuadrant ? pt.quadId === selectedQuadrant : true;
                const isHovered = hoveredPoint?.id === pt.id;

                return (
                  <g
                    key={pt.id}
                    className="cursor-pointer transition-all duration-200"
                    onMouseEnter={() => setHoveredPoint(pt)}
                    onMouseLeave={() => setHoveredPoint(null)}
                    style={{ opacity: isSelected ? 1 : 0.15 }}
                  >
                    {/* Halo suave al pasar el mouse */}
                    {isHovered && (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={12}
                        fill={pt.color}
                        fillOpacity={0.25}
                        className="animate-pulse"
                      />
                    )}
                    {/* Punto principal */}
                    <circle
                      cx={cx}
                      cy={cy}
                      r={isHovered ? 7.5 : 5}
                      fill={pt.color}
                      stroke="#FFFFFF"
                      strokeWidth={1.8}
                      className="shadow-sm"
                    />
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Tooltip Flotante de Anonimato Seguro */}
          {hoveredPoint && (
            <div
              className="absolute pointer-events-none z-20 bg-slate-900/95 text-white p-3 rounded-xl shadow-xl border border-slate-700/80 backdrop-blur-md text-xs space-y-1"
              style={{
                left: `${Math.min(scaleX(hoveredPoint.x) + 10, svgWidth - 180)}px`,
                top: `${Math.max(scaleY(hoveredPoint.y) - 60, 10)}px`,
              }}
            >
              <div className="flex items-center justify-between gap-3 border-b border-slate-700/60 pb-1">
                <span className="font-mono font-bold text-teal-300">
                  {hoveredPoint.code}
                </span>
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                  style={{
                    backgroundColor: `${hoveredPoint.color}25`,
                    color: hoveredPoint.color,
                  }}
                >
                  {dimension[hoveredPoint.quadId]?.badge}
                </span>
              </div>
              <div className="text-[11px] text-slate-300">
                <div><strong>{dimension.xLabel}:</strong> {hoveredPoint.x} {dimension.xUnit}</div>
                <div><strong>{dimension.yLabel}:</strong> {hoveredPoint.y} {dimension.yUnit}</div>
              </div>
              <div className="text-[10px] text-slate-400 pt-1 border-t border-slate-800">
                {dimension[hoveredPoint.quadId]?.action}
              </div>
            </div>
          )}

          {/* Leyenda de navegación */}
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 px-2">
            <span>Pasa el cursor sobre cualquier punto para ver sus coordenadas.</span>
            {selectedQuadrant && (
              <button
                type="button"
                onClick={() => setSelectedQuadrant(null)}
                className="text-teal-600 font-semibold hover:underline cursor-pointer"
              >
                Restablecer vista completa
              </button>
            )}
          </div>
        </div>

        {/* Columna Derecha: Resumen de los 4 Cuadrantes & Distribución */}
        <div className="lg:col-span-5 space-y-3">
          <div className="flex items-center justify-between text-xs text-slate-500 pb-1">
            <span className="font-bold text-slate-700 uppercase tracking-wider text-[11px]">
              Distribución por Cuadrante
            </span>
            <span className="text-[11px] text-slate-400">
              Haz clic para aislar puntos
            </span>
          </div>

          {/* Tarjeta Q1: Control Óptimo */}
          <div
            onClick={() => setSelectedQuadrant(selectedQuadrant === "q1" ? null : "q1")}
            className={`p-3.5 rounded-xl border transition-all cursor-pointer ${
              selectedQuadrant === "q1"
                ? "ring-2 ring-emerald-500 bg-emerald-50/70 border-emerald-300 shadow-sm"
                : "bg-emerald-50/30 border-emerald-100 hover:bg-emerald-50/60"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <h5 className="text-xs font-bold text-emerald-950">Q1: {summary.q1.title}</h5>
                <WidgetInfoTooltip
                  title={`Cuadrante Q1: ${summary.q1.title}`}
                  size="sm"
                  align="left"
                  what={`Colaboradores con ${dimension.xLabel} y ${dimension.yLabel} bajo control fisiológico.`}
                  calculation={`Eje X < ${dimension.xCut} ${dimension.xUnit} y Eje Y < ${dimension.yCut} ${dimension.yUnit}`}
                  standard="Guías Internacionales de Práctica Clínica y Metas de Prevención Primaria."
                  relevance="Población sin riesgo activo en esta dimensión. Enfoque en mantenimiento preventivo."
                />
              </div>
              <span className="text-sm font-extrabold text-emerald-800">
                {summary.q1.percent}% ({summary.q1.count})
              </span>
            </div>
            <p className="text-[11px] text-emerald-900/80 mt-1">{summary.q1.desc}</p>
            <div className="text-[10px] text-emerald-700 font-medium mt-2 pt-1 border-t border-emerald-200/50">
              Acción: {summary.q1.action}
            </div>
          </div>

          {/* Tarjeta Q2: Riesgo Aislado Eje Y */}
          <div
            onClick={() => setSelectedQuadrant(selectedQuadrant === "q2" ? null : "q2")}
            className={`p-3.5 rounded-xl border transition-all cursor-pointer ${
              selectedQuadrant === "q2"
                ? "ring-2 ring-amber-500 bg-amber-50/70 border-amber-300 shadow-sm"
                : "bg-amber-50/30 border-amber-100 hover:bg-amber-50/60"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                <h5 className="text-xs font-bold text-amber-950">Q2: {summary.q2.title}</h5>
                <WidgetInfoTooltip
                  title={`Cuadrante Q2: ${summary.q2.title}`}
                  size="sm"
                  align="left"
                  what={`Riesgo elevado en el Eje Y (${dimension.yLabel}) con Eje X (${dimension.xLabel}) bajo control.`}
                  calculation={`Eje X < ${dimension.xCut} ${dimension.xUnit} y Eje Y ≥ ${dimension.yCut} ${dimension.yUnit}`}
                  standard="Consensos específicos del biomarcador del Eje Y."
                  relevance="Permite intervenir de forma temprana sobre un factor aislado antes de que desencadene patología mixta."
                />
              </div>
              <span className="text-sm font-extrabold text-amber-800">
                {summary.q2.percent}% ({summary.q2.count})
              </span>
            </div>
            <p className="text-[11px] text-amber-900/80 mt-1">{summary.q2.desc}</p>
            <div className="text-[10px] text-amber-700 font-medium mt-2 pt-1 border-t border-amber-200/50">
              Acción: {summary.q2.action}
            </div>
          </div>

          {/* Tarjeta Q3: Riesgo Aislado Eje X */}
          <div
            onClick={() => setSelectedQuadrant(selectedQuadrant === "q3" ? null : "q3")}
            className={`p-3.5 rounded-xl border transition-all cursor-pointer ${
              selectedQuadrant === "q3"
                ? "ring-2 ring-orange-500 bg-orange-50/70 border-orange-300 shadow-sm"
                : "bg-orange-50/30 border-orange-100 hover:bg-orange-50/60"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                <h5 className="text-xs font-bold text-orange-950">Q3: {summary.q3.title}</h5>
                <WidgetInfoTooltip
                  title={`Cuadrante Q3: ${summary.q3.title}`}
                  size="sm"
                  align="left"
                  what={`Riesgo elevado en el Eje X (${dimension.xLabel}) con Eje Y (${dimension.yLabel}) bajo control.`}
                  calculation={`Eje X ≥ ${dimension.xCut} ${dimension.xUnit} y Eje Y < ${dimension.yCut} ${dimension.yUnit}`}
                  standard="Consensos específicos del biomarcador del Eje X."
                  relevance="Foco prioritario en hábitos, pausas activas o ajuste metabólico antes del compromiso vascular."
                />
              </div>
              <span className="text-sm font-extrabold text-orange-800">
                {summary.q3.percent}% ({summary.q3.count})
              </span>
            </div>
            <p className="text-[11px] text-orange-900/80 mt-1">{summary.q3.desc}</p>
            <div className="text-[10px] text-orange-700 font-medium mt-2 pt-1 border-t border-orange-200/50">
              Acción: {summary.q3.action}
            </div>
          </div>

          {/* Tarjeta Q4: Riesgo Mixto Prioritario */}
          <div
            onClick={() => setSelectedQuadrant(selectedQuadrant === "q4" ? null : "q4")}
            className={`p-3.5 rounded-xl border transition-all cursor-pointer ${
              selectedQuadrant === "q4"
                ? "ring-2 ring-rose-500 bg-rose-50/70 border-rose-300 shadow-sm"
                : "bg-rose-50/30 border-rose-100 hover:bg-rose-50/60"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                <h5 className="text-xs font-bold text-rose-950">Q4: {summary.q4.title}</h5>
                <WidgetInfoTooltip
                  title={`Cuadrante Q4: ${summary.q4.title}`}
                  size="sm"
                  align="left"
                  what="Riesgo compuesto o co-ocurrencia simultánea en ambos ejes clínicos evaluados."
                  calculation={`Eje X ≥ ${dimension.xCut} ${dimension.xUnit} y Eje Y ≥ ${dimension.yCut} ${dimension.yUnit}`}
                  standard="Criterios de Síndrome Metabólico (NCEP ATP III / IDF) y Guías de Riesgo Combinado OIT/OMS."
                  relevance="Población prioritaria para Salud Ocupacional con mayor probabilidad de ausentismo médico grave e incapacidades."
                />
              </div>
              <span className="text-sm font-extrabold text-rose-800">
                {summary.q4.percent}% ({summary.q4.count})
              </span>
            </div>
            <p className="text-[11px] text-rose-900/80 mt-1">{summary.q4.desc}</p>
            <div className="text-[10px] text-rose-700 font-medium mt-2 pt-1 border-t border-rose-200/50">
              Acción: {summary.q4.action}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
